import type { BoardProperty, Outcomes } from '../../@types/Board.js';
import type { ITSPayloadBoard } from '../../@types/ITSPayload.js';
import type { SystemType } from '../../@types/SystemType.js';
import { asStringArray } from '../../utils/asStringArray.js';
import { oneXTwoToBinary } from '../../utils/bitwise.js';
import BoardPoolgames from './BoardPoolgames.js';

export type ITS1X2Board = ITSPayloadBoard<{
  readonly boardData: (string | undefined)[];
  readonly boardProps: readonly BoardProperty[];
  readonly systemType: readonly [SystemType];
  readonly systemNum: number;
}>;

export type ITSUBoard = {
  readonly boardData: (string | undefined)[];
  readonly systemType: readonly ['USYS'];
};

class Board1x2 extends BoardPoolgames {
  private readonly _mathHedge: Outcomes = [];

  private _uOutcomes: Outcomes = [];

  get mathHedges(): Outcomes {
    return this._mathHedge;
  }

  get uOutcomes(): Outcomes {
    return this._uOutcomes;
  }

  /**
   * Takes systemNumber and if valid systemNumber it sets it for the board.
   *
   * @param systemNumber Number | systemNumber for given system
   * @returns instance
   */
  override setSystemNumber(systemNumber: number): this {
    // if (!systems.getSystemFromNumber(systemNumber)) {
    //   throw new WagerError({
    //     message: `Unsupported systemNumber: ${systemNumber}`,
    //     clientCode: WagerError.ErrorCodes.UNSUPPORTED_VALUE_SYSTEM_NUMBER,
    //   });
    // }
    super.setSystemNumber(systemNumber);

    return this;
  }

  override getRowCount(): number {
    let rowCount = super.getRowCount();

    for (const hedge of this._mathHedge) {
      const eventSignsCount = this.outcomes[hedge - 1]?.reduce((sum, outcomes) => sum + outcomes.length, 0) ?? 1;
      rowCount = rowCount * eventSignsCount;
    }

    return rowCount;
  }

  /**
   * Returns boardData for board. If systemType is RSYS or USYS it will add mathHedge to boardData.
   *
   * @returns Array with string | Array with all outcomes as string
   */
  getBoardData(): (string | undefined)[] {
    const outcomes = this.outcomes.map((outcomeGroup) =>
      [...outcomeGroup]
        .pop()
        ?.map((outcome) => ['1', 'X', '2'][outcome])
        .join('')
    );

    // if ((this.systemType === SystemType.RSYS || this.systemType === SystemType.USYS) && this.#mathHedge.length) {
    //   this.#mathHedge.forEach((hedgeEventNumber) => {
    //     if (!outcomes[hedgeEventNumber - 1].length > 1) {
    //       throw new WagerError({
    //         message: `Can only set math hedge to event with more than 1 outcome: ${outcomes[hedgeEventNumber - 1]}`,
    //         clientCode: WagerError.ErrorCodes.INCOMPLETE_BET,
    //       });
    //     }

    //     outcomes[hedgeEventNumber - 1] = `${outcomes[hedgeEventNumber - 1]}M`;
    //   });
    // }

    return outcomes;
  }

  /**
   * Takes eventNumber for for hedge to be put on (M "signs").
   *
   * @param eventNumber Number for the event
   * @returns instance
   */
  addMathHedge(eventNumber: number): this {
    this._mathHedge.push(eventNumber);

    return this;
  }

  /**
   * Sets correct index for outcome on event for u sign. Value is used on compose for bet.
   *
   * @param eventNumber Number for the event
   * @param outcomeIndex Number | Index for outcome
   * @returns instance
   */
  addUOutcome(eventNumber: number, outcomeIndex: number): this {
    if (this._uOutcomes.length === 0 || this._uOutcomes.length < eventNumber) {
      const nextArrayLength = eventNumber - this._uOutcomes.length;
      this._uOutcomes = [...this._uOutcomes, ...Array.from<number>({ length: nextArrayLength })];
    }

    this._uOutcomes.splice(eventNumber - 1, 1, outcomeIndex);

    return this;
  }

  /**
   * Used for tracking and logs.
   *
   * @returns String | String with boardData, boardProperties and systemType
   */
  override toString(): string {
    const logString = super.toString();

    const outcomeString = this.outcomes.map((board) => board.map((selection) => asStringArray(selection))).join(',');
    const logEntries = [`B:${outcomeString}`, logString];

    // if (this.systemType === SystemType.USYS && this.#uOutcomes.length) {
    //   const uOutcomeString = this.uOutcomes
    //     .map((uOutcome, index) => (uOutcome !== undefined ? `${index + 1}:${uOutcome}` : undefined))
    //     .filter((value) => value)
    //     .join(',');
    //   logEntries.push(`UB:${uOutcomeString}`);
    // }

    if (this.systemNumber && this._mathHedge.length > 0) {
      logEntries.push(`MH:[${this._mathHedge.join(',')}]`);
    }

    return logEntries.join(' ');
  }

  /**
   * Composes board for ITS-payment.
   *
   * @returns Object | Returns an object with correct data and structure for ITS-payment
   */
  compose(): ITS1X2Board {
    const boardData = this.getBoardData();

    const itsBoard = {
      boardData,
      boardProps: this.boardProperties,
      systemType: [this.systemType],
      systemNum: this.systemNumber || 1,
    } as const;

    type ITSBoard1x2 = ITSPayloadBoard<typeof itsBoard>;

    return itsBoard as ITSBoard1x2;
  }

  /**
   * Composes uOutcomes for board.
   *
   * @returns Returns uBoard with ITS structure;
   */
  composeUOutcomes(): ITSUBoard {
    const uSigns = this.uOutcomes;

    const boardData = this.outcomes.map((outcome, index) => {
      const uSign = uSigns[index];
      if (uSign !== undefined) {
        return ['1', 'X', '2'][uSign];
      }
      return '0';
    });

    const uBoard = {
      boardData,
      systemType: ['USYS'],
    } as const;

    return uBoard;
  }

  composeOwnRows(): ITSPayloadBoard<string> {
    const boardData = this.getBoardData();
    const convertedBoardData = boardData
      .filter(Boolean)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .map((outcome) => oneXTwoToBinary(outcome!))
      .join('');

    return convertedBoardData as ITSPayloadBoard<typeof convertedBoardData>;
  }
}

export default Board1x2;
